import {Component, effect, EffectCleanupRegisterFn, inject, model, ModelSignal, Signal} from '@angular/core';
import {EControlActions, IControlDataChanged, IFilter, INITIAL_STATE_TOKEN} from 'frontier/nucleus';
import {
  DefaultFormApiAdapter,
  DynamicFormComponent,
  DynamicFormControlComponent,
  IForm,
  IFormElement,
  IFormGroup,
  TAnyFormElement,
} from 'frontier/browserkit';
import {filter, takeUntil, tap} from 'rxjs/operators';
import {ProcessOverviewStore} from '../../../service/process-overview.store';
import {NgIf} from '@angular/common';
import {forkJoin, Subject} from 'rxjs';
import {IClientsTileApi} from '../clients-tile.interface';
import {ClientMessageMapper} from '../client-message-details/client-message.mapper';
import {TClientData} from '../client-message-details/client-message.interface';
import {IClientMessageDetails} from '../client-message-details/client-message-details.interface';
import {IDisplayOnlyFlag} from '../display-flag.interface';
import {NotBillableService} from 'src/app/api/generated';
import AppControlGuids from '../../../../../../api/ControlGuids';
import {ICart} from '../../../service/state.interface';
import {MatError} from '@angular/material/form-field';


class ConspicuityApiAdapter extends DefaultFormApiAdapter {
  displayOnlyFlags: IDisplayOnlyFlag[];
  isUncollectibleApprovable: boolean;

  override from(apiData: IForm): IForm {
    let formElements: TAnyFormElement[] = apiData.formGroups?.[0]?.elements ?? [];

    const fg: IFormGroup = {
      ...apiData.formGroups?.[0],
      elements: formElements.slice(0, 3), // [0]: Not billable, [1]: Is on hold, [2]: re-introduce on time
    };

    console.log(fg);

    this.isUncollectibleApprovable =
      (!formElements.find((fe: TAnyFormElement): boolean => fe.key === 'UncollectibleApproved;-1')?.value || false)
    && (!!formElements.find((fe: TAnyFormElement): boolean => fe.key === 'UncollectibleReason;-1')?.value || false);

    formElements = formElements.filter((fe: TAnyFormElement): boolean =>
    {
      return (fe.key !== 'UncollectibleApproved;-1') && !(fe.key === 'UncollectibleReason;-1' && !fe.value);
    });

    this.displayOnlyFlags = formElements.slice(3)
      .map((formElem: IFormElement): IDisplayOnlyFlag => {
        return {
          label: formElem.label,
          value: formElem.value,
        };
      });

    console.log(this.displayOnlyFlags);

    return {
      confirmable: apiData.confirmable,
      formGroups: [fg],
    };
  }
}

@Component({
  selector: 'app-not-billable-form',
  templateUrl: '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/form-control/dynamic-form/dynamic-form-control/dynamic-form-control.component.html',
  styleUrl: '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/form-control/dynamic-form/dynamic-form-control/dynamic-form-control.component.scss',
  standalone: true,
  imports: [NgIf, DynamicFormComponent, MatError],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}}
  ],
})
export class NotBillableFormComponent extends DynamicFormControlComponent {
  override GUID: string = AppControlGuids.TNotBillable;
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _notBillableService: NotBillableService = inject(NotBillableService);
  override apiAdapter: ConspicuityApiAdapter = new ConspicuityApiAdapter();
  numberOfWantedClients: ModelSignal<number> = model.required<number>();
  minScore: ModelSignal<number> = model.required<number>();
  availableClients: ModelSignal<number> = model.required<number>();
  selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;
  clientsMessage: ModelSignal<IClientMessageDetails> = model<IClientMessageDetails>(null);
  clients: ModelSignal<IClientsTileApi[]> = model.required<IClientsTileApi[]>();
  rowData: ModelSignal<IDisplayOnlyFlag[]> = model.required();
  displayOnlyFlags: ModelSignal<IDisplayOnlyFlag[]> = model.required<IDisplayOnlyFlag[]>();
  isCollectibleApprovable: ModelSignal<boolean> = model<boolean>(false);

  destroyed: Subject<boolean> = new Subject<boolean>();

  constructor() {
    super();

    effect(() => {
      const selectedCart: ICart = this.selectedCart();
      if (selectedCart) {
        // Patch the filter which triggers the effect below and sets loading true
        this.patchFilter({
          parent: selectedCart.obj,
          typename: 'cart',
        });
      }
    }, {allowSignalWrites: true});

    effect((onCleanup: EffectCleanupRegisterFn) => {
      onCleanup(() => {
        this.destroyed.next(true);
      })
      const isChanged: boolean = this.isChanged();
      const filter: IFilter = this.filter();

      // Ẁhen loading has finished, only then the parent of the instance is updated. Wait until loading finished before getting the clients.
      const loading: boolean = this.loading();

      if (loading === false && isChanged && filter.parent) {
        forkJoin([
          this._notBillableService.notBillableGetSimilarClients(
            {
              InstanceId: this.instanceId(),
              count: this.numberOfWantedClients(),
              minimumScore: this.minScore(),
            }
          ).pipe(
            tap((similarClients: IClientsTileApi[]) => {
              this.availableClients?.set(similarClients[0].HitRate);
              this.clients?.set([...similarClients]);
            })),
          this._notBillableService.notBillableGetClientMessage({
              InstanceId: this.instanceId()
            }
          ).pipe(
            tap((clientMessage: TClientData) => {
                if (clientMessage) {
                  this.clientsMessage?.set(ClientMessageMapper.toModel(clientMessage as TClientData));
                } else {
                  this.clientsMessage?.set(null);
                }
              }
            )),
        ]).pipe(
          takeUntil(this.destroyed)
        ).subscribe();
      }
    }, {
      allowSignalWrites: true
    });

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged) => {
          return new Map([
            [AppControlGuids.TAllClientList, new Set([EControlActions.attendance])],
            [null, new Set([EControlActions.resolveClientMessage])]
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap(() => {
          this.changeAndFetchInstance();
        })
      ).subscribe(),
    );
  }

  override toModel(data: IForm): any {
    const mapped = super.toModel(data);
    // Set the rows
    this.displayOnlyFlags?.set(this.apiAdapter.displayOnlyFlags);
    this.isCollectibleApprovable?.set(this.apiAdapter.isUncollectibleApprovable);
    this.rowData?.set(this.apiAdapter.displayOnlyFlags.map((i: IDisplayOnlyFlag): IDisplayOnlyFlag => {
      return {label: i.label, value: i.value === '0' ? 'nein' : i.value === '1' ? 'ja' : i.value};
    }));
    return mapped;
  }
}
